import { createContext } from 'reka-ui'
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { useLoading, useLocalStorage } from '@gat-ship/utils'
import { toggleCollapsedGroup as toggleCollapsedGroupName } from '../model/permission-view'

/**
 * UI-state factory for the access-role permissions view: search text,
 * delayed-loading flags, and the persisted collapse/hide-required state.
 * Created once by the view and shared with its descendants (permission
 * groups, items, the scroll-to sidebar) through {@link providePermissionsView}
 * / {@link usePermissionsView}.
 */
export function definePermissionsView() {
  const _search = ref('')
  const loadingState = useLoading()

  const localStorageState = useLocalStorage<{
    hideRequiredPermissions: boolean
    collapsedGroups: string[]
  }>('access-role-state', {
    hideRequiredPermissions: false,
    collapsedGroups: []
  })

  const collapsedGroupsState = computed(
    () => localStorageState.value.collapsedGroups
  )

  const hideRequiredPermissionsState = computed(
    () => localStorageState.value.hideRequiredPermissions
  )

  function setCollapsedGroups(collapsedGroups: string[]) {
    localStorageState.value.collapsedGroups = collapsedGroups
  }

  function toggleCollapsedGroup(groupName: string) {
    localStorageState.value.collapsedGroups = toggleCollapsedGroupName(
      localStorageState.value.collapsedGroups,
      groupName
    )
  }

  function setHideRequiredPermissions(hideRequiredPermissions: boolean) {
    localStorageState.value.hideRequiredPermissions = hideRequiredPermissions
  }

  return {
    localStorageState: {
      hideRequiredPermissions: hideRequiredPermissionsState,
      collapsedGroups: collapsedGroupsState,
      setCollapsedGroups,
      toggleCollapsedGroup,
      setHideRequiredPermissions
    },
    search: _search,
    isLoading: loadingState.isLoading,
    isDelayedLoading: loadingState.isDelayedLoading,
    setLoading: loadingState.setLoading
  }
}

export const [usePermissionsView, providePermissionsView] = createContext<{
  localStorageState: {
    hideRequiredPermissions: ComputedRef<boolean>
    collapsedGroups: ComputedRef<string[]>
    setCollapsedGroups: (collapsedGroups: string[]) => void
    toggleCollapsedGroup: (groupName: string) => void
    setHideRequiredPermissions: (hideRequiredPermissions: boolean) => void
  }
  search: Ref<string>
  isLoading: Ref<boolean>
  isDelayedLoading: Ref<boolean>
  setLoading: (isLoading: boolean) => void
}>('AccessRoleView')
